import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { LayoutGrid, List as ListIcon, Map as MapIcon, SlidersHorizontal, X, MapPin, BedDouble, Bath, Maximize } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { PropertyCard } from "../components/PropertyCard";
import { PROPERTY_TYPES, AMENITIES, typeLabel, formatPrice, statusColor, img } from "../lib/utils-app";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

export default function Listings({ mode }) {
  const { t, lang, L } = useLang();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    property_type: params.get("property_type") || "any",
    bedrooms: params.get("bedrooms") || "any",
    bathrooms: params.get("bathrooms") || "any",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    city: params.get("city") || "",
    amenities: [],
    sort: "recent",
  });

  const transaction = mode === "rent" ? "rent" : "sale";

  const fetchData = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    p.set("transaction_type", transaction);
    if (filters.q) p.set("q", filters.q);
    if (filters.property_type !== "any") p.set("property_type", filters.property_type);
    if (filters.bedrooms !== "any") p.set("bedrooms", filters.bedrooms);
    if (filters.bathrooms !== "any") p.set("bathrooms", filters.bathrooms);
    if (filters.min_price) p.set("min_price", filters.min_price);
    if (filters.max_price) p.set("max_price", filters.max_price);
    if (filters.city) p.set("city", filters.city);
    if (filters.amenities.length) p.set("amenities", filters.amenities.join(","));
    p.set("sort", filters.sort);
    const { data } = await api.get(`/properties?${p.toString()}`);
    setItems(data.items);
    setTotal(data.total);
    setLoading(false);
  }, [filters, transaction]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const toggleAmenity = (a) => setFilters((f) => ({
    ...f, amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
  }));
  const clear = () => setFilters({
    q: "", property_type: "any", bedrooms: "any", bathrooms: "any",
    min_price: "", max_price: "", city: "", amenities: [], sort: "recent",
  });

  const title = mode === "rent" ? t("nav.rent") : t("nav.buy");

  const FilterPanel = () => (
    <div className="space-y-6" data-testid="filter-panel">
      <div>
        <label className="label-caps text-muted-foreground">{t("search.city")}</label>
        <input value={filters.city} onChange={(e) => set("city", e.target.value)}
          placeholder="Miami, Coral Gables…" data-testid="filter-city"
          className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none transition-colors" />
      </div>
      <div>
        <label className="label-caps text-muted-foreground">{t("search.type")}</label>
        <Select value={filters.property_type} onValueChange={(v) => set("property_type", v)}>
          <SelectTrigger className="mt-2 rounded-none" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t("common.any")}</SelectItem>
            {PROPERTY_TYPES.map((pt) => <SelectItem key={pt} value={pt}>{typeLabel(pt, lang)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps text-muted-foreground">{t("search.beds")}</label>
          <Select value={filters.bedrooms} onValueChange={(v) => set("bedrooms", v)}>
            <SelectTrigger className="mt-2 rounded-none" data-testid="filter-beds"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("common.any")}</SelectItem>
              {[1, 2, 3, 4, 5].map((b) => <SelectItem key={b} value={String(b)}>{b}+</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps text-muted-foreground">{t("search.baths")}</label>
          <Select value={filters.bathrooms} onValueChange={(v) => set("bathrooms", v)}>
            <SelectTrigger className="mt-2 rounded-none" data-testid="filter-baths"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("common.any")}</SelectItem>
              {[1, 2, 3, 4].map((b) => <SelectItem key={b} value={String(b)}>{b}+</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps text-muted-foreground">{t("search.minPrice")}</label>
          <input type="number" value={filters.min_price} onChange={(e) => set("min_price", e.target.value)}
            data-testid="filter-min-price"
            className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none transition-colors" />
        </div>
        <div>
          <label className="label-caps text-muted-foreground">{t("search.maxPrice")}</label>
          <input type="number" value={filters.max_price} onChange={(e) => set("max_price", e.target.value)}
            data-testid="filter-max-price"
            className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none transition-colors" />
        </div>
      </div>
      <div>
        <label className="label-caps text-muted-foreground">{t("search.amenities")}</label>
        <div className="mt-3 grid grid-cols-2 gap-2.5">
          {AMENITIES.map((a) => (
            <label key={a} className="flex items-center gap-2 text-sm font-light cursor-pointer">
              <Checkbox checked={filters.amenities.includes(a)} onCheckedChange={() => toggleAmenity(a)} data-testid={`amenity-${a}`} />
              {a}
            </label>
          ))}
        </div>
      </div>
      <button onClick={clear} className="w-full py-2.5 border border-border hover:border-primary label-caps transition-colors" data-testid="clear-filters">{t("search.clear")}</button>
    </div>
  );

  return (
    <div className="pt-24">
      <div className="border-b border-border bg-card/40">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10">
          <h1 className="font-serif text-4xl md:text-5xl font-light">{title}</h1>
          <div className="mt-4 flex items-center gap-2 max-w-xl">
            <input value={filters.q} onChange={(e) => set("q", e.target.value)}
              placeholder={t("hero.searchPlaceholder")} data-testid="listings-search"
              className="flex-1 bg-background border border-border focus:border-primary px-4 py-3 text-sm outline-none transition-colors" />
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
        {/* Desktop filters */}
        <aside className="hidden lg:block sticky top-24 self-start">
          <div className="border border-border p-6"><FilterPanel /></div>
        </aside>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <p className="text-muted-foreground font-light">
              <span className="text-foreground font-medium">{total}</span> {t("search.results")}
            </p>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowFilters(true)} className="lg:hidden flex items-center gap-2 px-3 py-2 border border-border label-caps" data-testid="mobile-filters-btn">
                <SlidersHorizontal size={14} /> {t("search.moreFilters")}
              </button>
              <Select value={filters.sort} onValueChange={(v) => set("sort", v)}>
                <SelectTrigger className="w-44 rounded-none" data-testid="sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">{t("search.recent")}</SelectItem>
                  <SelectItem value="price_asc">{t("search.priceAsc")}</SelectItem>
                  <SelectItem value="price_desc">{t("search.priceDesc")}</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex border border-border">
                {[["grid", LayoutGrid], ["list", ListIcon], ["map", MapIcon]].map(([v, Icon]) => (
                  <button key={v} onClick={() => setView(v)}
                    className={`p-2.5 transition-colors ${view === v ? "bg-primary text-primary-foreground" : "hover:text-primary"}`}
                    data-testid={`view-${v}`}>
                    <Icon size={16} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/3] bg-card animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <p className="py-24 text-center text-muted-foreground font-light" data-testid="no-results">{t("common.noResults")}</p>
          ) : view === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="grid-view">
              {items.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
          ) : view === "list" ? (
            <div className="space-y-4" data-testid="list-view">
              {items.map((p) => (
                <Link key={p.id} to={`/property/${p.id}`} className="group flex flex-col sm:flex-row gap-5 bg-card border border-border hover:border-primary/40 transition-colors duration-300" data-testid={`list-item-${p.id}`}>
                  <div className="sm:w-72 aspect-[4/3] hover-zoom flex-shrink-0"><img src={img(p.images?.[0])} alt="" className="w-full h-full object-cover" /></div>
                  <div className="p-5 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-0.5 border ${statusColor(p.status)}`}>{p.status}</span>
                      <span className="label-caps text-muted-foreground">{typeLabel(p.property_type, lang)}</span>
                    </div>
                    <h3 className="font-serif text-2xl group-hover:text-primary transition-colors">{L(p.title)}</h3>
                    <p className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1"><MapPin size={14} className="text-primary" /> {p.neighborhood}, {p.city}</p>
                    <p className="font-serif text-2xl text-primary mt-3">{formatPrice(p.price, p.currency)}</p>
                    <div className="flex gap-5 text-sm text-muted-foreground mt-3">
                      {p.bedrooms > 0 && <span className="flex items-center gap-1.5"><BedDouble size={15} />{p.bedrooms} {t("common.bed")}</span>}
                      {p.bathrooms > 0 && <span className="flex items-center gap-1.5"><Bath size={15} />{p.bathrooms} {t("common.bath")}</span>}
                      {p.sqft > 0 && <span className="flex items-center gap-1.5"><Maximize size={15} />{p.sqft.toLocaleString()}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <MapView items={items} L={L} />
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-background border-l border-border p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl">{t("search.moreFilters")}</h3>
              <button onClick={() => setShowFilters(false)}><X /></button>
            </div>
            <FilterPanel />
          </div>
        </div>
      )}
    </div>
  );
}

function MapView({ items, L }) {
  const [active, setActive] = useState(items[0]?.id);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[600px]" data-testid="map-view">
      <div className="relative bg-card border border-border overflow-hidden">
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle at 30% 40%, rgba(212,175,55,0.08), transparent 40%), radial-gradient(circle at 70% 60%, rgba(212,175,55,0.06), transparent 40%)",
          backgroundColor: "hsl(var(--card))",
        }}>
          <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.3 }} />
        </div>
        {items.map((p, i) => {
          const x = 15 + ((p.lng ? (p.lng + 80.3) * 900 : (i * 37) % 70) % 70);
          const y = 15 + ((p.lat ? (26 - p.lat) * 600 : (i * 53) % 65) % 65);
          return (
            <button key={p.id} onClick={() => setActive(p.id)}
              className={`absolute -translate-x-1/2 -translate-y-1/2 px-2.5 py-1 text-xs font-medium transition-all duration-300 ${active === p.id ? "bg-primary text-primary-foreground z-20 scale-110" : "bg-background border border-primary/50 text-primary z-10"}`}
              style={{ left: `${Math.min(x, 88)}%`, top: `${Math.min(y, 85)}%` }}
              data-testid={`map-marker-${p.id}`}>
              ${Math.round(p.price / 1000)}K
            </button>
          );
        })}
      </div>
      <div className="overflow-y-auto space-y-3 pr-1">
        {items.map((p) => (
          <Link key={p.id} to={`/property/${p.id}`}
            className={`flex gap-3 bg-card border p-2 transition-colors duration-300 ${active === p.id ? "border-primary" : "border-border"}`}
            onMouseEnter={() => setActive(p.id)}>
            <img src={img(p.images?.[0])} alt="" className="w-28 h-24 object-cover flex-shrink-0" />
            <div className="py-1 min-w-0">
              <h4 className="font-serif text-lg truncate">{L(p.title)}</h4>
              <p className="text-primary font-serif text-lg">{formatPrice(p.price, p.currency)}</p>
              <p className="text-xs text-muted-foreground truncate">{p.neighborhood}, {p.city}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
